/* eslint-disable i18next/no-literal-string */
import {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useAppConfig} from '@/hooks/use-app-config'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {usePageTitle} from '@/hooks/use-page-title'
import {useTypedEntityAttributes} from '@/hooks/use-typed-entity-attributes'
import {routes} from '@/lib/routes'
import {Button} from '@/components/ui/button'
import {Card, CardHeader, CardTitle, CardContent} from '@/components/ui/card'
import {Textarea} from '@/components/ui/textarea'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {InlineRow} from '@/components/shared/InlineRow'
import {FormGrid} from '@/components/shared/FormGrid'
import {FormField} from '@/components/shared/FormField'
import {PageBackButton} from '@/components/shared/PageBackButton'
import {SectionContainer} from '@/components/shared/SectionContainer'
import {OwnersPicker} from '@/components/shared/OwnersPicker'
import {AttributeFields} from '@/components/attributes/AttributeFields'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {AttributeValueType} from '@/types/attribute'
import type {AttributeDefinition, SetAttributeRequest} from '@/types/attribute-definition'
import {FunctionGroupResponseFields} from '@/types/function-group'
import type {FunctionGroupCreateRequest} from '@/types/function-group'
import {useFormSubmit} from '@/hooks/use-form-submit'
import type {InitialOwnerRequest} from '@/types/owner'

function buildSetAttributeRequest(def: AttributeDefinition, raw: string): SetAttributeRequest {
    const value = raw.trim()
    switch (def.valueType) {
        case AttributeValueType.NUMBER:
            return {attributeName: def.name, valueType: def.valueType, valueNumber: Number(value)}
        case AttributeValueType.BOOLEAN:
            // eslint-disable-next-line i18next/no-literal-string -- boolean string-form sentinel from <input>
            return {attributeName: def.name, valueType: def.valueType, valueBoolean: value === 'true'}
        case AttributeValueType.DATE:
            return {attributeName: def.name, valueType: def.valueType, valueDate: value}
        case AttributeValueType.TIMESTAMP:
            return {attributeName: def.name, valueType: def.valueType, valueTimestamp: value}
        case AttributeValueType.STRING:
        case AttributeValueType.JSON:
        case AttributeValueType.REFERENCE:
            return {attributeName: def.name, valueType: def.valueType, valueString: value}
    }
}

/**
 * Create Function Group page invoked from
 * /access-governance/function-groups/new. The user picks the function-group
 * type up-front; the form then renders the type-scoped dynamic attribute
 * fields (including the required {@code name} attribute that backs the
 * type's {@code displayNameTemplate}).
 */
export function CreateFunctionGroup() {
    const services = useServices()
    const i18n = useI18n()
    const navigate = useNavigate()
    const {functionGroupTypes, functionGroupResponsibilityTypes} = useAppConfig()

    const pageTitle = i18n.functionGroups.createFunctionGroup
    usePageTitle(pageTitle)

    const [functionGroupType, setFunctionGroupType] = useState('')
    const [description, setDescription] = useState('')
    const [attributeValues, setAttributeValues] = useState<Record<string, string>>({})
    // WHY: catalog-ownership/§7 — multi-owner at create time. Start with one
    // empty BUSINESS row; user can pick the Party and add further owners.
    const [owners, setOwners] = useState<InitialOwnerRequest[]>([
        // eslint-disable-next-line i18next/no-literal-string -- responsibility is a backend type code, not user-visible text
        {ownerPartyId: '', responsibility: 'BUSINESS'},
    ])
    const [ownerLabels, setOwnerLabels] = useState<Record<string, string>>({})

    const {formDefinitions, templateAttributeNames, isLoading} = useTypedEntityAttributes({
        entityType: AttributeEntityType.FUNCTION_GROUP,
        typeCode: functionGroupType,
    })

    const renderDefinitions = formDefinitions.filter((def) => {
        const isStatic = def.createdAt === '1970-01-01T00:00:00Z'
        const isExcluded =
            def.name === FunctionGroupResponseFields.name ||
            def.name === FunctionGroupResponseFields.functionGroupType ||
            def.name === FunctionGroupResponseFields.description
        return !(isStatic && isExcluded)
    })

    const createMutation = useEntityMutation({
        mutationFn: (request: FunctionGroupCreateRequest) => services.functionGroups.create(request),
        successMessage: i18n.functionGroups.createSuccess,
        onSuccess: (fg) => {
            navigate(routes.accessGovernance.functionGroups.detail(fg.id))
        },
    })

    const {submit, fieldErrors} = useFormSubmit({
        fields: [
            {
                id: 'fg-type',
                label: i18n.common.type,
                value: functionGroupType,
                required: true,
                focusId: 'fg-type',
            },
            ...renderDefinitions.map((def) => {
                const label = def.displayName || def.name
                const value = attributeValues[def.id] ?? ''
                const focusId = `attr-${def.name}`
                return {
                    id: def.id,
                    label,
                    value,
                    required: def.required || templateAttributeNames.includes(def.name),
                    focusId,
                }
            }),
        ],
        onSubmit: () => {
            const attributes: SetAttributeRequest[] = renderDefinitions
                .map((def) => {
                    const raw = attributeValues[def.id] ?? ''
                    if (!raw.trim()) return null
                    return buildSetAttributeRequest(def, raw)
                })
                .filter((r): r is SetAttributeRequest => r != null)

            createMutation.mutate({
                functionGroupType,
                description: description.trim() || null,
                owners,
                autoAssignable: false,
                requestable: true,
                expiryRequired: false,
                attributes,
            })
        },
    })

    return (
        <SectionContainer>
            <PageBackButton to={routes.accessGovernance.functionGroups.list()}/>

            <Card className="max-w-2xl">
                <CardHeader>
                    <CardTitle data-testid="card-title-create-function-group">{pageTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={submit}>
                        <FormGrid>
                            <FormField
                                label={i18n.common.type}
                                htmlFor="fg-type"
                                required
                                error={fieldErrors['fg-type']}
                            >
                                <Select value={functionGroupType} onValueChange={setFunctionGroupType}>
                                    <SelectTrigger id="fg-type" data-testid="select-fg-type">
                                        <SelectValue placeholder={i18n.common.type}/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {functionGroupTypes.map((t) => (
                                            <SelectItem key={t.code} value={t.code} data-testid={`option-${t.code}`}>
                                                {t.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormField>
                            <FormField label={i18n.common.description} htmlFor="fg-description">
                                <Textarea
                                    id="fg-description"
                                    data-testid="input-fg-description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </FormField>
                            <AttributeFields
                                definitions={renderDefinitions}
                                values={attributeValues}
                                onChange={(defId, v) =>
                                    setAttributeValues((s) => ({...s, [defId]: v}))}
                                templateAttributeNames={templateAttributeNames}
                                errors={fieldErrors}
                            />
                            <FormField label={i18n.functionGroups.owner} htmlFor="fg-owners" required>
                                <OwnersPicker
                                    owners={owners}
                                    onOwnersChange={setOwners}
                                    responsibilityTypes={functionGroupResponsibilityTypes}
                                    ownerLabels={ownerLabels}
                                    onOwnerLabelsChange={setOwnerLabels}
                                />
                            </FormField>
                        </FormGrid>

                        <InlineRow className="justify-end pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                data-testid="btn-cancel-create-function-group"
                                onClick={() => navigate(routes.accessGovernance.functionGroups.list())}
                            >
                                {i18n.common.cancel}
                            </Button>
                            <Button
                                type="submit"
                                data-testid="btn-submit-create-function-group"
                                disabled={isLoading || createMutation.isPending}
                            >
                                {pageTitle}
                            </Button>
                        </InlineRow>
                    </form>
                </CardContent>
            </Card>
        </SectionContainer>
    )
}
